.container {
  /* `flex` (not `inline-flex`) is the right default for a fill-width
   * widget — the SearchBox stretches to its container's width via
   * `width: 100%`, and `inline-flex` would fight that by participating
   * in inline formatting context with content-driven natural sizing.
   * `flex` produces an identical layout in a block parent without the
   * impedance mismatch. */
  display: flex;
  align-items: center;
  gap: var(--spacing-8);
  /* Figma spec: padding 8 / 12, fully-rounded pill, --bg-secondary
   * background. Width is fluid (no fixed 328px) — consumers control the
   * width via the parent layout, e.g. wrapping the SearchBox in a Box
   * with style={{ maxWidth: 328 }} for the Figma-default sizing. */
  padding: var(--spacing-8) var(--spacing-12);
  border-radius: var(--radius-round);
  background: var(--bg-secondary);
  width: 100%;
  box-sizing: border-box;
  /* No explicit border — Figma spec doesn't have one. The pill sits
   * on a primary surface and the bg-secondary tone provides enough
   * contrast for the search affordance.
   *
   * Container only animates the focus ring (box-shadow) and bg.
   * The icon color transition lives on .searchIcon directly — a
   * parent's `color` transition does NOT animate a descendant's
   * overridden color, so declaring `transition: color` here would
   * be a silent no-op against the .searchIcon override below. */
  transition:
    background-color var(--motion-duration-fast) ease,
    box-shadow var(--motion-duration-fast) ease;
}

/* Keyboard focus ring matches the Input primitive's pattern: a 3px
 * brand-coloured halo on focus-within. The Figma SearchBox spec
 * doesn't define a Focus state explicitly (the "Active" variant in
 * the file shows only the native caret), but a real keyboard user
 * needs an affordance on the pill itself — without this, a user
 * tabbing into the SearchBox sees only the caret blink and no
 * change to the container. */
.container:focus-within {
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand) 18%, transparent);
}

/* Disabled state: dim the entire pill (background + icon + text)
 * uniformly so the half-disabled "input dimmed but icon and bg full
 * brightness" appearance can't happen. Set on the container by the
 * component when the `disabled` prop is true. The .input:disabled
 * rule below adds a not-allowed cursor when hovering the input
 * itself; the container opacity covers everything else. */
.containerDisabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Search icon: muted by default (matches placeholder tone), brightens
 * to --text-primary on focus or when the input has typed content.
 * Uses :focus-within (parent has a focused descendant) and :has
 * (parent has an input with non-placeholder content). :has is
 * Chrome-only fallback-free, which is fine — this is a Chrome
 * extension and we ship Chromium-only.
 *
 * The transition lives ON .searchIcon (not on the parent .container),
 * because a parent's `color` transition does not propagate to a
 * descendant whose color is overridden directly. With the rule here,
 * the icon fades smoothly from --text-tertiary to --text-primary
 * when the field gains focus or content. */
.searchIcon {
  color: var(--text-tertiary);
  transition: color var(--motion-duration-fast) ease;
}
.container:focus-within .searchIcon,
.container:has(.input:not(:placeholder-shown)) .searchIcon {
  color: var(--text-primary);
}

.input {
  flex: 1 0 0;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: var(--text-primary);
  font: var(--text-body-regular);
  padding: 0;
  /* Strip the WebKit search-field chrome (the magnifier glass and
   * built-in clear X button). We render our own icons. */
  -webkit-appearance: none;
  appearance: none;
}

.input::placeholder {
  color: var(--text-tertiary);
  /* Placeholder text inherits font-* from the input, no need to
   * re-declare. opacity:1 normalises across browsers (Firefox uses
   * opacity 0.54 by default; Chrome 1, but we're Chrome-only so
   * this is belt-and-braces). */
  opacity: 1;
}

/* Strip the native WebKit search-cancel button — we have our own.
 * `display: none` fully removes it from the layout and rendering
 * tree, so no further appearance reset is needed. */
.input::-webkit-search-cancel-button {
  display: none;
}

.input:disabled {
  cursor: not-allowed;
}

/* The clear button is rendered via the IconButton primitive, which
 * brings its own focus ring + variant styles. We override its margin
 * to keep it flush against the right edge of the pill. */
.clearButton {
  margin: calc(-1 * var(--spacing-4));
  margin-inline-start: 0;
}
